import React, { useState } from 'react';
import {
  Calendar,
  Clock,
  Clipboard,
  AlertTriangle,
  Building,
  FileText,
  MapPin,
  CheckCircle,
  XCircle,
  HelpCircle,
  User,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '../../ui/card';
import { Button } from '../../ui/button';
import { useTranslationWithLanguage } from '../../../context/language-context';

// Define the interface for the visit summary data
export interface VisitSummaryData {
  // Date & Time
  date: string;
  time: string;

  // Task Information
  taskId: string;
  taskType: string;
  taskStatus: string;
  riskScore: string;

  // Incident Information (if applicable)
  incident?: {
    subject: string;
    date: string;
    description: string;
    riskFactors: string[];
  };

  // Establishment Details
  establishment: {
    name: string;
    id: string;
    licenseNumber: string;
    licenseExpiry: string;
    type: string;
    nationality: string;
  };

  // Location Details
  location: {
    emirate: string;
    zone: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };

  // Inspection Summary
  inspectionSummary: {
    totalQuestions: number;
    answeredQuestions: number;
    compliantAnswers: number;
    nonCompliantAnswers: number;
    naAnswers: number;
  };

  // Question Details
  questions: {
    id: number;
    question: string;
    answer: string;
    isCompliant: boolean;
  }[];
}

export interface VisitSummaryCardProps {
  summaryData: VisitSummaryData | undefined;
  language?: string;
  onSubmit?: (comment: string) => void;
}

export const VisitSummaryCard: React.FC<VisitSummaryCardProps> = ({
  summaryData,
  language = 'en',
  onSubmit,
}) => {
  const { t } = useTranslationWithLanguage();
  const [inspectorComment, setInspectorComment] = useState('');

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(inspectorComment);
    }
  };

  // Function to get risk color based on score
  const getRiskColor = (score: string): string => {
    switch (score.toUpperCase()) {
      case 'A':
        return 'bg-green-800 text-white'; // Best - Green
      case 'B':
        return 'bg-green-500 text-white'; // Light Green
      case 'C':
        return 'bg-yellow-500 text-white'; // Yellow
      case 'D':
        return 'bg-orange-500 text-white'; // Orange
      case 'E':
        return 'bg-red-400 text-white'; // Light Red
      case 'F':
        return 'bg-red-700 text-white'; // Worst - Red
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {language === 'en'
          ? 'Inspection Visit Summary'
          : 'ملخص زيارة التفتيش'}
      </h1>

      {/* Date & Time Card */}
      <Card className="overflow-hidden border-t-4 border-t-[#b89256]">
        <CardHeader className="bg-gray-50">
          <CardTitle className="text-xl flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-[#b89256]" />
            {language === 'en' ? 'Date & Time' : 'التاريخ والوقت'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2 text-gray-500" />
              <span className="font-medium mr-2">
                {language === 'en' ? 'Date:' : 'التاريخ:'}
              </span>
              <span>{summaryData.date}</span>
            </div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2 text-gray-500" />
              <span className="font-medium mr-2">
                {language === 'en' ? 'Time:' : 'الوقت:'}
              </span>
              <span>{summaryData.time}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Task Information Card */}
      <Card className="overflow-hidden border-t-4 border-t-[#b89256]">
        <CardHeader className="bg-gray-50">
          <CardTitle className="text-xl flex items-center">
            <Clipboard className="w-5 h-5 mr-2 text-[#b89256]" />
            {language === 'en'
              ? 'Task Information'
              : 'معلومات المهمة'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <span className="font-medium mr-2">
                {language === 'en' ? 'Task ID:' : 'رقم المهمة:'}
              </span>
              <span className="text-blue-600 font-semibold">
                {summaryData.taskId}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-medium mr-2">
                {language === 'en' ? 'Type:' : 'النوع:'}
              </span>
              <span>{summaryData.taskType}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium mr-2">
                {language === 'en' ? 'Status:' : 'الحالة:'}
              </span>
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {summaryData.taskStatus}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-medium mr-2">
                {language === 'en' ? 'Risk Score:' : 'درجة المخاطر:'}
              </span>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(
                  summaryData.riskScore
                )}`}
              >
                {summaryData.riskScore}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Incident Information Card (if applicable) */}
      {summaryData.incident && (
        <Card className="overflow-hidden border-t-4 border-t-red-500">
          <CardHeader className="bg-red-50">
            <CardTitle className="text-xl flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-red-500" />
              {language === 'en'
                ? 'Incident Information'
                : 'معلومات الحادث'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-center">
                <span className="font-medium mr-2">
                  {language === 'en' ? 'Subject:' : 'الموضوع:'}
                </span>
                <span className="font-semibold">
                  {summaryData.incident.subject}
                </span>
              </div>
              <div className="flex items-center">
                <span className="font-medium mr-2">
                  {language === 'en' ? 'Date:' : 'التاريخ:'}
                </span>
                <span>{summaryData.incident.date}</span>
              </div>
              <div>
                <span className="font-medium block mb-2">
                  {language === 'en' ? 'Description:' : 'الوصف:'}
                </span>
                <p className="text-gray-700 bg-gray-50 p-3 rounded-md">
                  {summaryData.incident.description}
                </p>
              </div>
              <div>
                <span className="font-medium block mb-2">
                  {language === 'en'
                    ? 'Risk Factors:'
                    : 'عوامل الخطر:'}
                </span>
                <ul className="list-disc list-inside space-y-1">
                  {summaryData.incident.riskFactors.map(
                    (factor, index) => (
                      <li key={index} className="text-gray-700">
                        {factor}
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Establishment Details Card */}
      <Card className="overflow-hidden border-t-4 border-t-[#b89256]">
        <CardHeader className="bg-gray-50">
          <CardTitle className="text-xl flex items-center">
            <Building className="w-5 h-5 mr-2 text-[#b89256]" />
            {language === 'en'
              ? 'Establishment Details'
              : 'تفاصيل المنشأة'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-1 md:col-span-2">
              <span className="font-medium mr-2">
                {language === 'en' ? 'Name:' : 'الاسم:'}
              </span>
              <span className="font-semibold text-lg">
                {summaryData.establishment.name}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-medium mr-2">
                {language === 'en' ? 'ID:' : 'الرقم التعريفي:'}
              </span>
              <span className="text-blue-600">
                {summaryData.establishment.id}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-medium mr-2">
                {language === 'en'
                  ? 'License Number:'
                  : 'رقم الرخصة:'}
              </span>
              <span>{summaryData.establishment.licenseNumber}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium mr-2">
                {language === 'en'
                  ? 'License Expiry:'
                  : 'تاريخ انتهاء الرخصة:'}
              </span>
              <span className="text-amber-600 font-medium">
                {summaryData.establishment.licenseExpiry}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-medium mr-2">
                {language === 'en' ? 'Type:' : 'النوع:'}
              </span>
              <span>{summaryData.establishment.type}</span>
            </div>
            <div className="flex items-center">
              <User className="w-4 h-4 mr-2 text-gray-500" />
              <span className="font-medium mr-2">
                {language === 'en' ? 'Nationality:' : 'الجنسية:'}
              </span>
              <span>{summaryData.establishment.nationality}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Location Details Card */}
      <Card className="overflow-hidden border-t-4 border-t-[#b89256]">
        <CardHeader className="bg-gray-50">
          <CardTitle className="text-xl flex items-center">
            <MapPin className="w-5 h-5 mr-2 text-[#b89256]" />
            {language === 'en' ? 'Location Details' : 'تفاصيل الموقع'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <span className="font-medium mr-2">
                {language === 'en' ? 'Emirate:' : 'الإمارة:'}
              </span>
              <span>{summaryData.location.emirate}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium mr-2">
                {language === 'en' ? 'Zone:' : 'المنطقة:'}
              </span>
              <span>{summaryData.location.zone}</span>
            </div>
            <div className="col-span-1 md:col-span-2 flex items-center">
              <span className="font-medium mr-2">
                {language === 'en' ? 'Coordinates:' : 'الإحداثيات:'}
              </span>
              <span className="text-gray-600">
                {summaryData.location.coordinates.latitude},{' '}
                {summaryData.location.coordinates.longitude}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inspection Summary Card */}
      <Card className="overflow-hidden border-t-4 border-t-[#b89256]">
        <CardHeader className="bg-gray-50">
          <CardTitle className="text-xl flex items-center">
            <FileText className="w-5 h-5 mr-2 text-[#b89256]" />
            {language === 'en'
              ? 'Inspection Summary'
              : 'ملخص التفتيش'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 text-center">
              <div className="text-2xl font-bold text-blue-600">
                {summaryData.inspectionSummary.answeredQuestions} /{' '}
                {summaryData.inspectionSummary.totalQuestions}
              </div>
              <div className="text-sm text-blue-700 mt-1">
                {language === 'en'
                  ? 'Questions Answered'
                  : 'الأسئلة المجابة'}
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-100 text-center">
              <div className="text-2xl font-bold text-green-600">
                {summaryData.inspectionSummary.compliantAnswers}
              </div>
              <div className="text-sm text-green-700 mt-1">
                {language === 'en' ? 'Compliant' : 'متوافق'}
              </div>
            </div>

            <div className="bg-red-50 p-4 rounded-lg border border-red-100 text-center">
              <div className="text-2xl font-bold text-red-600">
                {summaryData.inspectionSummary.nonCompliantAnswers}
              </div>
              <div className="text-sm text-red-700 mt-1">
                {language === 'en' ? 'Non-Compliant' : 'غير متوافق'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Question Details Card */}
      <Card className="overflow-hidden border-t-4 border-t-[#b89256]">
        <CardHeader className="bg-gray-50">
          <CardTitle className="text-xl flex items-center">
            <Clipboard className="w-5 h-5 mr-2 text-[#b89256]" />
            {language === 'en'
              ? 'Question Details'
              : 'تفاصيل الأسئلة'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="divide-y divide-gray-200">
            {summaryData.questions.map((question) => {
              let answerColor = 'text-gray-400';
              let answerBg = 'bg-gray-50';
              let Icon = HelpCircle;

              if (
                question.answer === 'Yes' ||
                question.answer === 'Compliant'
              ) {
                answerColor = 'text-green-600';
                answerBg = 'bg-green-50';
                Icon = CheckCircle;
              } else if (
                question.answer === 'No' ||
                question.answer === 'Non-Compliant'
              ) {
                answerColor = 'text-red-600';
                answerBg = 'bg-red-50';
                Icon = XCircle;
              } else if (question.answer === 'N/A') {
                answerColor = 'text-[#b89256]';
                answerBg = 'bg-[#f8f3e9]';
                Icon = HelpCircle;
              }

              return (
                <div
                  key={question.id}
                  className="py-3 first:pt-0 last:pb-0"
                >
                  <div className="flex items-start">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800 mb-1">
                        {question.question}
                      </p>
                    </div>
                    <div
                      className={`ml-3 px-3 py-1 rounded-full text-xs font-medium flex items-center ${answerBg} ${answerColor}`}
                    >
                      <Icon className="w-3 h-3 mr-1" />
                      {question.answer}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Inspector Comment Card */}
      <Card className="overflow-hidden border-t-4 border-t-[#b89256]">
        <CardHeader className="bg-gray-50">
          <CardTitle className="text-xl flex items-center">
            <User className="w-5 h-5 mr-2 text-[#b89256]" />
            {language === 'en' ? 'Inspector Comment' : 'تعليق المفتش'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <textarea
            rows={5}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-[#b89256] focus:border-[#b89256] font-mono text-sm"
            placeholder={
              language === 'en'
                ? 'Enter any additional observations or notes here…'
                : 'أدخل أي ملاحظات أو ملاحظات إضافية هنا...'
            }
            value={inspectorComment}
            onChange={(e) => setInspectorComment(e.target.value)}
            aria-label={
              language === 'en' ? 'Inspector comment' : 'تعليق المفتش'
            }
          />
        </CardContent>
        <CardFooter className="bg-gray-50 border-t border-gray-200">
          <Button
            onClick={handleSubmit}
            className="ml-auto px-4 py-2 bg-[#b89256] rounded-md flex items-center space-x-1 text-white hover:bg-[#a17d45] shadow-sm"
          >
            {language === 'en' ? 'Submit Comment' : 'إرسال التعليق'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default VisitSummaryCard;
